<template>
  <div
    class="min-h-screen bg-gradient-to-br from-green-50 to-slate-100 relative overflow-hidden"
  >
    <!-- Background Elements -->
    <div class="absolute inset-0 overflow-hidden">
      <!-- Large corner circles -->
      <div
        class="absolute -top-32 -right-32 w-96 h-96 md:w-[500px] md:h-[500px] bg-green-800/5 rounded-full blur-xl transform-gpu animate-subtle-float"
      ></div>
      <div
        class="absolute -bottom-32 -left-32 w-96 h-96 md:w-[500px] md:h-[500px] bg-[#164364]/10 rounded-full blur-xl transform-gpu animate-subtle-float-reverse"
      ></div>

      <!-- Additional shapes -->
      <div
        class="absolute top-1/3 -right-16 w-64 h-64 bg-green-800/5 rounded-full blur-lg transform-gpu animate-subtle-pulse"
      ></div>

      <!-- Grid Pattern -->
      <div
        class="absolute inset-0 opacity-[0.02]"
        style="
          background-image: radial-gradient(#2f855a 0.5px, transparent 0.5px);
          background-size: 30px 30px;
          background-position: center center;
        "
      ></div>
    </div>

    <div class="container mx-auto px-4 py-12 relative z-10">
      <div class="grid md:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
        <!-- Left Column -->
        <div class="space-y-8 animate-fade-in">
          <h1 class="text-5xl text-[#164364] leading-tight font-bold">
            Connect With Our Farm Experts
          </h1>
          <p class="text-gray-700 text-lg">
            Have questions about our agricultural solutions? Our team of farming
            specialists is here to help you maximize your yields and streamline
            your operations.
          </p>
          <div class="space-y-6">
            <div class="flex items-center space-x-4">
              <div class="bg-green-800 p-3 rounded-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <h3 class="font-medium text-[#164364]">Email Us</h3>
                <p class="text-gray-700">support@filmaghana.com</p>
              </div>
            </div>
            <div class="flex items-center space-x-4">
              <div class="bg-green-800 p-3 rounded-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 class="font-medium text-[#164364]">Visit Us</h3>
                <p class="text-gray-700">Accra, Ghana</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Form -->
        <div
          class="bg-white rounded-3xl shadow-xl p-8 animate-slide-up hover:shadow-2xl transition-shadow duration-300 border border-green-100"
        >
          <h2 class="text-2xl font-bold text-[#164364] mb-6">
            Get Agricultural Support
          </h2>
          <form @submit.prevent="handleSubmit" class="space-y-6">
            <div class="grid md:grid-cols-2 gap-6">
              <div class="form-group">
                <label class="form-label">Full Name</label>
                <input
                  v-model="name"
                  type="text"
                  class="form-input"
                  placeholder="Enter your name"
                  required
                />
              </div>
              <div class="form-group">
                <label class="form-label">Email</label>
                <input
                  v-model="email"
                  type="email"
                  class="form-input"
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>

            <div class="grid md:grid-cols-2 gap-6">
              <div class="form-group">
                <label class="form-label">Phone Number</label>
                <input
                  v-model="phone"
                  type="tel"
                  class="form-input"
                  placeholder="Your phone number"
                  required
                />
              </div>
              <div class="form-group">
                <label class="form-label">Farm Type</label>
                <select v-model="farmType" class="form-input" required>
                  <option value="" disabled selected>Select farm type</option>
                  <option value="crops">Crop Farming</option>
                  <option value="livestock">Livestock</option>
                  <option value="mixed">Mixed Farming</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">How can we help you?</label>
              <textarea
                v-model="message"
                class="form-input min-h-[150px]"
                placeholder="Describe your farming needs or questions..."
                rows="4"
              ></textarea>
            </div>

            <button
              type="submit"
              class="group relative overflow-hidden w-full bg-green-800 hover:bg-green-700 text-white px-8 py-4 rounded-xl transition-all duration-300"
              :disabled="isSubmitting"
            >
              <span
                class="absolute top-0 left-0 w-full h-full bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"
              ></span>
              <span class="relative flex items-center justify-center z-10">
                {{ isSubmitting ? "Sending..." : "Send Message" }}
                <svg
                  v-if="!isSubmitting"
                  class="ml-2 w-5 h-5 transform transition-transform duration-300 group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  ></path>
                </svg>
                <svg
                  v-else
                  class="animate-spin ml-2 w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </span>
            </button>
          </form>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <div
      v-if="showSuccessModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm animate-modal-appear"
    >
      <div
        class="bg-white p-12 rounded-3xl shadow-2xl text-center max-w-md animate-modal-content"
      >
        <div class="mb-6">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-24 w-24 mx-auto text-green-600 animate-bounce"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h2
          class="text-3xl font-bold text-[#164364] mb-4 animate-fade-in-delay"
        >
          Thank You, Farmer!
        </h2>
        <p class="text-gray-700 mb-6 animate-fade-in-delay-2">
          Your message has been sent. Our agricultural experts will get back to
          you soon with tailored solutions for your farm.
        </p>
        <button
          @click="closeModal"
          class="group relative overflow-hidden bg-green-800 hover:bg-green-700 text-white px-8 py-3 rounded-xl transition-all duration-300 animate-fade-in-delay-3"
        >
          <span
            class="absolute top-0 left-0 w-full h-full bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"
          ></span>
          <span class="relative z-10">Close</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AgriculturalContactForm",
  data() {
    return {
      name: "",
      email: "",
      phone: "",
      farmType: "",
      message: "",
      isSubmitting: false,
      showSuccessModal: false,
    };
  },
  methods: {
    async handleSubmit() {
      // Prevent multiple submissions
      if (this.isSubmitting) return;

      // Start submission process
      this.isSubmitting = true;

      try {
        const response = await fetch(
          "https://formspree.io/f/xpwqfgfgfssssssssssfgsdfgkqgl",
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              name: this.name,
              email: this.email,
              phone: this.phone,
              farmType: this.farmType,
              message: this.message,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        // Reset form and show success modal
        this.resetForm();
        this.showSuccessModal = true;
      } catch (error) {
        // You might want to show an error modal or message here
      } finally {
        this.isSubmitting = false;
      }
    },
    resetForm() {
      this.name = "";
      this.email = "";
      this.phone = "";
      this.farmType = "";
      this.message = "";
    },
    closeModal() {
      this.showSuccessModal = false;
    },
  },
};
</script>

<style scoped>
.form-group {
  @apply transition-all duration-300;
}

.form-label {
  @apply block text-[#164364] font-medium text-sm mb-2;
}

.form-input {
  @apply w-full p-3 border border-green-100 rounded-xl focus:ring-2 focus:ring-green-500 
  focus:border-transparent outline-none transition-all duration-300;
}

.animate-fade-in {
  animation: fadeIn 1s ease-out;
}

.animate-slide-up {
  animation: slideUp 0.8s ease-out;
}

.animate-modal-appear {
  animation: modalAppear 0.5s ease-out;
}

.animate-modal-content {
  animation: modalContent 0.6s ease-out;
}

.animate-fade-in-delay {
  animation: fadeIn 0.8s ease-out 0.2s forwards;
  opacity: 0;
}

.animate-fade-in-delay-2 {
  animation: fadeIn 0.8s ease-out 0.4s forwards;
  opacity: 0;
}

.animate-fade-in-delay-3 {
  animation: fadeIn 0.8s ease-out 0.6s forwards;
  opacity: 0;
}

/* Background animations */
@keyframes subtle-float {
  0%,
  100% {
    transform: translateY(0) translateX(0);
  }
  50% {
    transform: translateY(-10px) translateX(5px);
  }
}

@keyframes subtle-float-reverse {
  0%,
  100% {
    transform: translateY(0) translateX(0);
  }
  50% {
    transform: translateY(10px) translateX(-5px);
  }
}

@keyframes subtle-pulse {
  0%,
  100% {
    opacity: 0.7;
    transform: scale(1);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.05);
  }
}

.animate-subtle-float {
  animation: subtle-float 12s ease-in-out infinite;
}

.animate-subtle-float-reverse {
  animation: subtle-float-reverse 12s ease-in-out infinite;
}

.animate-subtle-pulse {
  animation: subtle-pulse 8s ease-in-out infinite;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes modalAppear {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes modalContent {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.form-input:hover {
  @apply border-green-200;
}

.form-group:focus-within {
  transform: translateY(-2px);
}
</style>
